import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi, readAuth, writeAuth } from "@/lib/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => readAuth());
  const [booting, setBooting] = useState(true);

  // On boot, if we have a token, verify it via /me. If invalid, wipe.
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      if (!auth?.access_token) {
        setBooting(false);
        return;
      }
      try {
        const user = await authApi.me();
        if (cancelled) return;
        const next = { ...auth, user };
        writeAuth(next);
        setAuth(next);
      } catch {
        if (cancelled) return;
        writeAuth(null);
        setAuth(null);
      } finally {
        if (!cancelled) setBooting(false);
      }
    };
    boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    return authApi.login(email, password);
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    return authApi.adminLogin(email, password);
  }, []);

  const register = useCallback(async (payload) => {
    return authApi.register(payload);
  }, []);

  const verify2FA = useCallback(async (pending_token, code, channel) => {
    const data = channel
      ? await authApi.verify2faCode(pending_token, channel, code)
      : await authApi.verify2fa(pending_token, code);
    const next = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: data.user,
    };
    writeAuth(next);
    setAuth(next);
    return data;
  }, []);

  const send2FACode = useCallback(async (pending_token, channel) => {
    return authApi.send2faCode(pending_token, channel);
  }, []);

  const adminVerify2FA = useCallback(async (pending_token, code) => {
    const data = await authApi.adminVerify2fa(pending_token, code);
    const next = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: data.user,
    };
    writeAuth(next);
    setAuth(next);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout(auth?.refresh_token || "");
    } catch {
      // ignore — we're clearing state anyway
    }
    writeAuth(null);
    setAuth(null);
  }, [auth?.refresh_token]);

  // Phase 10E — called by VerifyContactPage after /auth/register/verify-contact.
  // The user's tokens are issued in one shot (no second verify-code step), so
  // we need to hydrate the auth context directly from the response payload.
  const completeAuth = useCallback((data) => {
    const next = {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: data.user,
    };
    writeAuth(next);
    setAuth(next);
    return next;
  }, []);

  const setUser = useCallback((patch) => {
    setAuth((prev) => {
      if (!prev) return prev;
      const nextUser = typeof patch === "function"
        ? patch(prev.user)
        : { ...(prev.user || {}), ...patch };
      const next = { ...prev, user: nextUser };
      writeAuth(next);
      return next;
    });
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const user = await authApi.me();
      setUser(user);
      return user;
    } catch { return null; }
  }, [setUser]);

  const value = useMemo(
    () => ({
      user: auth?.user || null,
      accessToken: auth?.access_token || null,
      refreshToken: auth?.refresh_token || null,
      isAuthenticated: !!auth?.access_token,
      isAdmin: !!auth?.user?.is_admin,
      booting,
      login,
      adminLogin,
      register,
      verify2FA,
      send2FACode,
      adminVerify2FA,
      logout,
      completeAuth,
      setUser,
      refreshMe,
    }),
    [auth, booting, login, adminLogin, register, verify2FA, send2FACode, adminVerify2FA, logout, completeAuth, setUser, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
